import React, { useMemo, useState } from "react";
import moment, { Moment } from "moment";
import { isEmpty, uniqBy } from "lodash";
import cns from "classnames";
import { Table, TableCell, TableRow, makeStyles, TableBody } from "@material-ui/core";
import "react-datepicker/dist/react-datepicker.css";
import { ConsultantUpcomingWorkplace } from "@bonliva-crm/graphql";
import { durationToWeeks, weekYearToDateLocal } from "@bonliva-crm/common";
import { AvailabilityCalendarEvent, generateWeeks, getCellCalendarEvents } from "./utils";
import { YearHeader } from "./year-header";
import { CalendarCell } from "./calendar-cell";
import { WorkplaceTitle } from "./workplace-title";
import { SelectionState } from "./selection-state";
import { UnderscoreWrapper } from "./underscore-wrapper";
import { Color } from "../../../theme";

type Props = {
  preSelectedDates: AvailabilityCalendarEvent[];
  keepSelected?: boolean;
  upcomingWorkplaces: ConsultantUpcomingWorkplace[];
  initialDate?: Date;
  selectableDates?: Date[];
  hideYearNumber?: boolean;

  onSelect: (weeks: Date[]) => void;
};

export const DragNSelectCalendar = (props: Props) => {
  const classes = useStyles();

  const weeks = useMemo(
    () => generateWeeks(props.initialDate ?? new Date()),
    [props.initialDate]
  );

  const selectableWeeks = useMemo(() => {
    return props.selectableDates?.map((x) => moment(x).isoWeek()) ?? [];
  }, [props.selectableDates]);

  const [selectedWeeks, setSelectedWeeks] = useState<Date[]>([]);
  const [selectionState, setSelectionState] = useState<SelectionState | null>(null);
  const [isMouseDown, setIsMouseDown] = useState(false);

  const onMouseDown = (date: Moment) => {
    setIsMouseDown(true);

    const type = selectedWeeks.find((x) => x.valueOf() === date.valueOf())
      ? "subtract"
      : "add";

    setSelectionState(SelectionState.create(type, date, date));
  };

  const onMouseUp = (date: Moment) => {
    setIsMouseDown(false);

    if (!selectionState?.start || !date) {
      return;
    }

    let start = selectionState.start;
    let end = date;

    if (start.isAfter(end)) {
      start = date;
      end = selectionState.start;
    }

    const weeks = durationToWeeks(start.toDate(), end.toDate(), false);

    let newSelectedWeeks: Date[];

    if (selectionState.type === "add") {
      newSelectedWeeks = uniqBy([...selectedWeeks, ...weeks], (x) => x.valueOf());
    } else {
      newSelectedWeeks = selectedWeeks.filter(
        (x) => !weeks.find((w) => w.toISOString() === x.toISOString())
      );
    }

    setSelectedWeeks(newSelectedWeeks);
    setSelectionState(null);

    props.onSelect?.(newSelectedWeeks);

    if (!props.keepSelected) {
      setTimeout(() => {
        setSelectedWeeks([]);
      }, 250);
    }
  };

  const onMouseEnter = (date: Moment) => {
    if (isMouseDown && selectionState) {
      setSelectionState(
        SelectionState.create(selectionState.type, selectionState.start, date)
      );
    }
  };

  const onMouseExit = () => {
    if (isMouseDown && !props.keepSelected) {
      setSelectionState(null);
    }
  };

  function getWorkplaces(week: Date) {
    return (props.upcomingWorkplaces ?? []).filter(
      (x) => weekYearToDateLocal(x.week).valueOf() === week.valueOf()
    );
  }

  function getStructure() {
    let prevYear = "";
    let prevMonth = "";
    let prevSelected = false;

    const selectedTs = [...selectedWeeks, ...(selectionState?.selectedWeeks || [])].map(
      (x) => x.valueOf()
    );

    const years: Array<{
      yearTitle: string;
      months: Array<{ month: string; cells: React.ReactNode[] }>;
    }> = [];

    for (let i = 0; i < weeks.length; i++) {
      const week = weeks[i];

      if (prevYear !== week.yearName) {
        years.push({
          yearTitle: week.yearName,
          months: []
        });
      }

      const year = years[years.length - 1];

      if (prevMonth !== week.monthName || prevYear !== week.yearName) {
        year.months.push({
          month: week.monthName,
          cells: []
        });
      }

      const workplaces = getWorkplaces(week.weekDate.toDate());

      const cellEvents = getCellCalendarEvents(
        props.preSelectedDates,
        week.weekDate.toDate()
      );

      const isSelected = selectedTs.includes(week.weekDate.valueOf());
      const isNextSelected = selectedTs.includes(weeks[i + 1]?.weekDate.valueOf());
      const isSelectedInner = isSelected && prevSelected;
      const isSelectedOuter =
        (isSelected && !prevSelected) || (isSelected && !isNextSelected);

      const isDisabled = !isEmpty(props.selectableDates)
        ? !selectableWeeks.includes(week.weekNum)
        : false;

      const cell = (
        <CalendarCell
          disabled={isDisabled}
          title={week.weekNum.toString()}
          tooltipTitle={
            !isEmpty(workplaces) ? <WorkplaceTitle items={workplaces} /> : null
          }
          onMouseDown={() => onMouseDown(week.weekDate)}
          onMouseUp={() => onMouseUp(week.weekDate)}
          onMouseEnter={() => onMouseEnter(week.weekDate)}
          isSelectedOuter={isSelectedOuter}
          isSelectedInner={isSelectedInner}
          extra={<UnderscoreWrapper calendarEvents={cellEvents} />}
        />
      );

      const month = year.months[year.months.length - 1];

      if (month) {
        month.cells.push(cell);
      }

      prevYear = week.yearName;
      prevMonth = week.monthName;
      prevSelected = isSelected;
    }

    return years;
  }

  const structure = getStructure();

  return (
    <Table className={classes.table} onMouseLeave={onMouseExit}>
      <TableBody>
        {structure.map((year) => {
          return (
            <React.Fragment key={year.yearTitle}>
              {!props.hideYearNumber && <YearHeader title={year.yearTitle} />}

              {year.months.map((month) => {
                return (
                  <TableRow key={month.month}>
                    <TableCell className={cns(classes.tableCell, classes.month)}>
                      {month.month}
                    </TableCell>

                    {month.cells.map((cell, i) => {
                      return React.cloneElement(cell as any, { key: month.month + i });
                    })}
                  </TableRow>
                );
              })}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

const useStyles = makeStyles(() => ({
  tableCell: {
    border: "none",
    padding: "8px",
    userSelect: "none"
  },
  month: {
    textTransform: "uppercase",
    color: Color.DetailsInput
  },
  table: {
    width: "90%"
  }
}));
